// src/pages/dashboard/operator_detail_admin.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Button,
  Divider,
  TextField,
  TableRow,
  TableCell,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import SortableTable, { HeadCell } from "../../components/SortableTable";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  getAllShapes,
  normalizeToLocaleDate,
} from "../../api/stats";
import { getUserCredits } from "../../api/credits";
import { getUser } from "../../api/users";

type Period = "30" | "60" | "90" | "total" | "custom";

type ShapeRow = {
  id_shape: number;
  data: string;
  nome: string;
  cognome: string;
};

const cardSx = {
  bgcolor: "background.paper",
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "none",
};

function normalizeDate(d?: string): Date | null {
  if (!d) return null;
  const date = new Date(d);
  return isNaN(date.getTime()) ? null : date;
}

function isInRange(d: Date, start?: Date | null, end?: Date | null): boolean {
  if (!start && !end) return true;
  const t = d.getTime();
  if (start && t < start.getTime()) return false;
  if (end && t > end.getTime()) return false;
  return true;
}

function monthsBetween(start: Date, end: Date): number {
  // Calcolo mesi effettivi tra due date (almeno 1)
  const years = end.getFullYear() - start.getFullYear();
  const months = years * 12 + (end.getMonth() - start.getMonth());
  return Math.max(1, months + 1);
}

const OperatorDetailAdmin: React.FC = () => {
  const { id_utente } = useParams<{ id_utente: string }>();
  const idOperatore = Number(id_utente);
  const navigate = useNavigate();
  const location = useLocation();

  const baseState = (location.state || {}) as { nome_completo?: string; email?: string };
  const [operatorName, setOperatorName] = useState<string>(baseState.nome_completo || "");
  const [operatorEmail, setOperatorEmail] = useState<string>(baseState.email || "");

  const [period, setPeriod] = useState<Period>("total");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const [credits, setCredits] = useState<number>(0);
  const [rows, setRows] = useState<ShapeRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Calcolo range periodo
  const rangeDates = useMemo(() => {
    if (period === "custom" && fromDate && toDate) {
      return {
        start: normalizeDate(fromDate),
        end: normalizeDate(toDate),
      };
    }
    if (period === "30" || period === "60" || period === "90") {
      const days = Number(period);
      const now = new Date();
      const start = new Date();
      start.setHours(0,0,0,0);
      start.setDate(now.getDate() - (days - 1));
      return { start, end: now };
    }
    return { start: null, end: null }; // total
  }, [period, fromDate, toDate]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        // 1) query shape (tutte), filtrate per creator
        const allShapes = await getAllShapes(true, null);
        const filteredForOperator = allShapes.filter((s) => Number(s.id_creatore || 0) === idOperatore);

        // 2) apply range filter
        const inRange = filteredForOperator.filter((s) => {
          const d = normalizeDate(s.timestamp_creazione || s.created_at || s.data_creazione);
          return d && isInRange(d, rangeDates.start, rangeDates.end);
        });

        const mapped: ShapeRow[] = inRange.map((s) => ({
          id_shape: s.id_shape,
          data: normalizeToLocaleDate(s.timestamp_creazione || s.created_at || s.data_creazione) ?? "",
          nome: s.nome || "",
          cognome: s.cognome || "",
        }));

        // ordino desc
        mapped.sort((a, b) => {
          const da = new Date(a.data.split("/").reverse().join("-")).getTime();
          const db = new Date(b.data.split("/").reverse().join("-")).getTime();
          return db - da;
        });

        setRows(mapped);

        // 3) credits residui dell'operatore
        const cred = await getUserCredits(idOperatore);
        setCredits(cred);

        // 4) info operatore se non passate nello state
        if (!operatorName || !operatorEmail) {
          const u = await getUser(idOperatore);
          const fullname = `${u.nome ?? ""} ${u.cognome ?? ""}`.trim() || `Operatore #${u.id_utente}`;
          setOperatorName(fullname);
          setOperatorEmail(u.email || "");
        }
      } catch (err) {
        console.error("Errore caricamento dettaglio operatore:", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [idOperatore, rangeDates, operatorName, operatorEmail]);

  // KPI
  const totalShapes = rows.length;

  const thisMonthCount = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    return rows.filter((r) => {
      const d = new Date(r.data.split("/").reverse().join("-"));
      return d.getFullYear() === y && d.getMonth() === m;
    }).length;
  }, [rows]);

  const lastMonthCount = useMemo(() => {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const y = prev.getFullYear();
    const m = prev.getMonth();
    return rows.filter((r) => {
      const d = new Date(r.data.split("/").reverse().join("-"));
      return d.getFullYear() === y && d.getMonth() === m;
    }).length;
  }, [rows]);

  const monthlyAvg = useMemo(() => {
    if (!rangeDates.start || !rangeDates.end) {
      // "total" -> calcoliamo su intero dataset: min/max date
      // Notare che rows già filtra per creatore, ma non per time...
      // fallback: usiamo 1 mese per evitare NaN
      if (!rows.length) return 0;
      const minD = rows.reduce((min, r) => {
        const d = new Date(r.data.split("/").reverse().join("-"));
        return d < min ? d : min;
      }, new Date());
      const maxD = rows.reduce((max, r) => {
        const d = new Date(r.data.split("/").reverse().join("-"));
        return d > max ? d : max;
      }, new Date(0));
      const months = monthsBetween(minD, maxD);
      return Number((rows.length / months).toFixed(2));
    }
    const months = monthsBetween(rangeDates.start, rangeDates.end);
    return Number((rows.length / months).toFixed(2));
  }, [rows, rangeDates]);

  const headCells: HeadCell<ShapeRow>[] = [
    { id: "data", label: "Data" },
    { id: "nome", label: "Nome Paziente" },
    { id: "cognome", label: "Cognome Paziente" },
  ];

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "calc(100vh - 64px)" }}>
      {/* Titolo + sottotitolo */}
      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2, pb: 1 }}>
        <Typography variant="h5" sx={{ color: "#fff", fontWeight: 800 }}>
          Operatore: {operatorName || `#${idOperatore}`}
        </Typography>
        <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.9)" }}>
          Monitoraggio attività e consumi crediti di questo operatore.
        </Typography>
        {operatorEmail && (
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
            {operatorEmail}
          </Typography>
        )}
      </Box>

      <Container maxWidth="xl" sx={{ pb: 3 }}>
        {/* Filtro periodo */}
        <Card sx={{ ...cardSx, p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <CalendarMonthOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1 }}>
                Periodo
              </Typography>
              {(["30", "60", "90", "total"] as Period[]).map((v) => (
                <Button
                  key={v}
                  size="small"
                  variant={period === v ? "contained" : "outlined"}
                  color={period === v ? "primary" : "inherit"}
                  sx={{ borderRadius: 999, textTransform: "none" }}
                  onClick={() => setPeriod(v)}
                >
                  {v === "30"
                    ? "Ultimi 30 giorni"
                    : v === "60"
                    ? "Ultimi 60 giorni"
                    : v === "90"
                    ? "Ultimi 90 giorni"
                    : "Totale"}
                </Button>
              ))}
            </Stack>
            {/* Range personalizzato */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <TextField
                size="small"
                label="Dal"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="Al"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                size="small"
                variant="contained"
                sx={{ textTransform: "none", borderRadius: 999 }}
                onClick={() => fromDate && toDate && setPeriod("custom")}
                disabled={!fromDate || !toDate}
              >
                Applica
              </Button>
              <Button
                size="small"
                variant="outlined"
                sx={{ textTransform: "none", borderRadius: 999 }}
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                  setPeriod("total");
                }}
              >
                Reset
              </Button>
            </Stack>
          </Stack>
        </Card>

        {/* KPI */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ ...cardSx, p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle1">Shape nel periodo</Typography>
                <AssignmentTurnedInOutlinedIcon color="action" />
              </Stack>
              <Typography variant="h4" fontWeight={800}>{totalShapes}</Typography>
              <Typography variant="body2" color="text.secondary">Totale analisi registrate</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ ...cardSx, p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle1">Media mensile</Typography>
                <AssignmentTurnedInOutlinedIcon color="action" />
              </Stack>
              <Typography variant="h4" fontWeight={800}>{monthlyAvg}</Typography>
              <Typography variant="body2" color="text.secondary">Media di shape/Mese</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ ...cardSx, p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle1">Crediti residui</Typography>
                <MonetizationOnIcon color="action" />
              </Stack>
              <Typography variant="h4" fontWeight={800}>{credits.toFixed(2)}</Typography>
              <Typography variant="body2" color="text.secondary">Disponibili per nuove analisi</Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Tabella shape nel periodo + quick stats mese */}
        <Card sx={{ ...cardSx }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1">Attività shape</Typography>
            <Typography variant="body2" color="text.secondary">
              Questo mese: <b>{thisMonthCount}</b> · Mese scorso: <b>{lastMonthCount}</b>
            </Typography>
          </Box>
          <Divider />
          <CardContent>
            <SortableTable<ShapeRow>
              rows={rows}
              headCells={[
                { id: "data", label: "Data" },
                { id: "nome", label: "Nome Paziente" },
                { id: "cognome", label: "Cognome Paziente" },
              ] as HeadCell<ShapeRow>[]}
              renderRow={(row) => (
                <TableRow key={row.id_shape}>
                  <TableCell>{row.data}</TableCell>
                  <TableCell>{row.nome}</TableCell>
                  <TableCell>{row.cognome}</TableCell>
                </TableRow>
              )}
              rowsPerPageOptions={[10, 25, 50]}
              initialRowsPerPage={25}
            />
            <Box mt={2}>
              <Button variant="outlined" onClick={() => navigate(-1)} sx={{ textTransform: "none", borderRadius: 999 }}>
                Torna Indietro
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default OperatorDetailAdmin;
